import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { toggleWelcomeGuideVisibility } from '@repo/redux/actions/ui';
import renderOption, { MenuOption } from '@views/CommandBar/Option';
import { UserAction } from '@repo/utils/types';
import {
  actionsListToSearchableSection,
  filterMenuSection,
} from '@views/CommandBar/locallySearchableSections';
import { Dispatch } from 'redux';
import { appVersion } from '@app/config';
import { capitalize } from '@repo/utils/helpers/string';
import styles from './Support.module.scss';
import { supportHref } from '@repo/utils/helpers/preferred-email-client';
import { SearchableOptionsModal } from '@repo/components/modals';

type MenuOptionsList = MenuOption[];
export type MenuSection = null | { title: string; suggestions: MenuOptionsList };

type UserIssue = 'bug' | 'feedback' | 'question';

const emailFooter = (issue: UserIssue) => {
  let version = `Web: ${appVersion}`;
  if (window.app.isElectron && window.desktopIntegrations) {
    version = `${version} (Desktop: ${window.desktopIntegrations.appInfo.version})`;
  }

  return `


---
Version: ${version}
User Agent: ${window.navigator?.userAgent}
Tags: #${issue}
---
  `;
};

const mailtoHref = (issue: UserIssue): string =>
  supportHref({
    subject: `Clay ${capitalize(issue)}`,
    body: `${encodeURIComponent(emailFooter(issue))}`,
  });

const actions = (dispatch: Dispatch, navigate: NavigateFunction): UserAction[] => [
  {
    title: 'Learn more',
    action: () => window.open('https://library.clay.earth/', '_blank'),
    type: 'action',
    subtitle: 'In-depth guides and FAQs',
  },
  {
    title: "What's New",
    action: () => window.open('https://clay.earth/new', '_blank'),
    type: 'action',
    subtitle: 'Updates and improvements',
  },
  {
    title: 'Welcome Guide',
    action: () => dispatch(toggleWelcomeGuideVisibility(true)),
    type: 'action',
    subtitle: 'Your introduction to Clay',
  },
  {
    title: 'App Tour',
    action: () => navigate('/tour'),
    type: 'action',
    subtitle: "A quick guide to Clay's major features",
  },
  {
    title: 'Report an issue',
    action: () => window.open(mailtoHref('bug'), window.app?.isElectron ? '_self' : '_blank'),
    type: 'action',
    subtitle: 'For bugs, crashes, and data issues',
  },
  {
    title: 'Give feedback',
    action: () => window.open(mailtoHref('feedback'), window.app?.isElectron ? '_self' : '_blank'),
    type: 'action',
    subtitle: 'Suggest a new feature or improvement',
  },
  {
    title: 'Ask a question',
    action: () => window.open(mailtoHref('question'), window.app?.isElectron ? '_self' : '_blank'),
    type: 'action',
    subtitle: 'Let us know how we can help',
  },
];

export const SupportModal = ({ closeModal }: { closeModal: () => void }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchableSection = useMemo(
    () => actionsListToSearchableSection([actions(dispatch, navigate), 'Support']),
    [navigate],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [actionSections, setActionSections] = useState([
    filterMenuSection(searchableSection, searchTerm, true),
  ]);

  const filterActionSections = useCallback((term: string) => {
    const filtered = filterMenuSection(searchableSection, term, true);
    return filtered ? [filtered] : [];
  }, []);

  const onOptionSelected = useCallback((option) => {
    closeModal();
    option.action();
  }, []);

  const handleSearch = useCallback((term) => setActionSections(filterActionSections(term)), []);

  return (
    <SearchableOptionsModal
      searchTerm={searchTerm}
      closeModal={closeModal}
      setSearchTerm={setSearchTerm}
      optionSections={actionSections}
      onOptionSelected={onOptionSelected}
      handleSearch={handleSearch}
      renderOption={renderOption}
      optionClassName={styles.option}
      overlayClassName={styles.overlay}
      placeholder="How can we help?"
    />
  );
};
